import { FunctionComponent } from "react";

import { classNames } from "@/utils/classNames";

import {
	KnowledgeDbTab,
	KNOWLEDGE_DB_TAB_LABELS,
	TabStatus,
	ContentStatus,
} from "../schema/knowledgeDb";

interface KnowledgeDbTabsProps {
	activeTab: KnowledgeDbTab;
	tabStatuses: TabStatus[];
	onTabChange: (tab: KnowledgeDbTab) => void;
}

const TAB_ORDER: KnowledgeDbTab[] = [
	KnowledgeDbTab.PAIN_POINTS,
	KnowledgeDbTab.PRODUCTS_SERVICES,
	KnowledgeDbTab.CASE_STUDIES,
	KnowledgeDbTab.OBJECTION_HANDLING,
];

export const KnowledgeDbTabs: FunctionComponent<KnowledgeDbTabsProps> = ({
	activeTab,
	tabStatuses,
	onTabChange,
}) => {
	const getStatus = (tab: KnowledgeDbTab) => tabStatuses.find((ts) => ts.tab === tab)?.status;

	return (
		<div className="border-b border-gray-200 bg-white">
			<nav aria-label="Knowledge DB tabs" className="flex gap-1 -mb-px">
				{TAB_ORDER.map((tab) => {
					const isActive = activeTab === tab;
					const status = getStatus(tab);

					return (
						<button
							key={tab}
							className={classNames(
								"flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
								isActive
									? "border-brand-light text-brand-light"
									: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
							)}
							type="button"
							onClick={() => onTabChange(tab)}
						>
							{KNOWLEDGE_DB_TAB_LABELS[tab]}
							{status && <StatusBadge status={status} />}
						</button>
					);
				})}
			</nav>
		</div>
	);
};

const StatusBadge: FunctionComponent<{ status: ContentStatus }> = ({ status }) => {
	if (status === ContentStatus.AI_DRAFTED) {
		return (
			<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-white border border-blue-action/40 text-blue-action">
				✨ AI-drafted
			</span>
		);
	}

	if (status === ContentStatus.DRAFT) {
		return (
			<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-status-warning-light border border-status-warning/40 text-status-warning">
				✎ Draft
			</span>
		);
	}

	return (
		<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-status-success-light border border-status-success/40 text-status-success">
			✓ Human-approved
		</span>
	);
};
